import { AlertCircle } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import {
  useRequirementLabels,
  type PublishRequirement,
} from "@/lib/recipes/publish-requirements";

interface RecipeMissingInfoBannerProps {
  /**
   * What the recipe is still missing, as the API's rule module reported it.
   * The walkthrough's complaint was that this banner appeared with nothing
   * marked as missing anywhere — so it now names each one (T-704).
   */
  missing?: PublishRequirement[];
  /** An override for callers with something more specific to say. */
  message?: string;
}

export function RecipeMissingInfoBanner({ missing, message }: RecipeMissingInfoBannerProps) {
  const { t } = useLingui();
  const labels = useRequirementLabels();

  return (
    <div className="flex items-center overflow-hidden rounded-[8px] border border-(--color-warning)/20 bg-(--color-warning-bg) p-3">
      <div className="flex flex-1 items-center gap-3">
        <div className="flex shrink-0 items-center rounded-[8px] bg-(--color-warning) p-3">
          <AlertCircle className="size-6 text-white" />
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-base font-medium text-(--color-text-primary) leading-none">
            {t`Missing Information`}
          </p>
          {message ? (
            <p className="text-sm text-(--color-text-secondary) leading-none">{message}</p>
          ) : missing && missing.length > 0 ? (
            <>
              <p className="text-sm text-(--color-text-secondary) leading-none">
                {t`This recipe still needs:`}
              </p>
              <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-(--color-text-secondary)">
                {missing.map((requirement) => (
                  <li key={requirement} className="list-disc list-inside">
                    {labels[requirement]}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-sm text-(--color-text-secondary) leading-none">
              {t`Please complete all required fields to publish the recipe`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
